async function ensureNodeImagesLoaded(node: HTMLElement): Promise<void> {
  const images = Array.from(node.querySelectorAll('img')) as HTMLImageElement[];
  if (images.length === 0) return;

  await Promise.all(
    images.map(async (img) => {
      // Wait for the resource to actually finish first. We can't rely on
      // img.decode() alone — some mobile/in-app browsers resolve it before
      // the image has loaded, which bakes a half-rendered card into the
      // exported image.
      if (!img.complete) {
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      }

      // Best-effort decode so the bitmap is ready for canvas drawing.
      try {
        if (typeof img.decode === 'function') await img.decode();
      } catch {}
    }),
  );
}

export type NodeToImageOptions = {
  pixelRatio?: number;
};

export async function nodeToBlob(node: HTMLElement, opts?: NodeToImageOptions): Promise<Blob> {
  // Prefer a modest default pixel ratio for speed; allow override via opts
  const dpr = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1;
  const defaultPixelRatio = Math.min(1.5, dpr);
  const requestedPixelRatio = typeof opts?.pixelRatio === 'number' ? opts.pixelRatio : undefined;
  const effectivePixelRatio = Math.max(1, Math.min(requestedPixelRatio ?? defaultPixelRatio, 3));
  const { toCanvas } = await import('html-to-image');
  // Ensure web fonts are fully loaded before rendering to avoid fallback fonts
  try {
    // @ts-ignore - document.fonts is widely supported in modern browsers
    if (typeof document !== 'undefined' && (document as any).fonts?.ready) {
      await (document as any).fonts.ready;
    }
  } catch {}
  await ensureNodeImagesLoaded(node);
  const renderOptions = {
    pixelRatio: effectivePixelRatio,
    cacheBust: false,
    // Do not skip fonts so that numeric/brand fonts are embedded correctly
    skipFonts: false,
    // Exclude elements marked with data-ignore-capture from the exported image
    filter: (domNode: HTMLElement) => {
      try {
        if (
          domNode instanceof Element &&
          domNode.hasAttribute('data-ignore-capture')
        ) {
          return false;
        }
      } catch {}

      return true;
    },
  };

  // Warm-up pass: mobile Safari / in-app WebViews frequently draw embedded
  // bitmaps (the card's background/pepe, now WebP) blank on the FIRST
  // toCanvas because they haven't been decoded yet. Render once to prime the
  // decode cache, wait a couple frames, then render the real one. This is the
  // documented html-to-image workaround for missing images on Safari.
  try {
    await toCanvas(node, renderOptions);
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  } catch {}

  const canvas = await toCanvas(node, renderOptions);
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
      'image/png',
    );
  });

  return blob;
}

export async function downloadNodeAsPng(node: HTMLElement, fileName = 'share.png', opts?: NodeToImageOptions) {
  const blob = await nodeToBlob(node, opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function copyNodeImageToClipboard(node: HTMLElement, opts?: NodeToImageOptions) {
  const blob = await nodeToBlob(node, opts);
  // Safari requires type to be image/png
  const item = new ClipboardItem({ [blob.type || 'image/png']: blob });
  await navigator.clipboard.write([item]);
}

export async function nodeToShareFile(
  node: HTMLElement,
  fileName = 'leverup-share.png',
  opts?: NodeToImageOptions,
): Promise<File> {
  const blob = await nodeToBlob(node, opts);

  return new File([blob], fileName, { type: blob.type || 'image/png' });
}

/**
 * Whether the browser can share a PNG file via the native share sheet.
 * Used to switch "Copy Image" (desktop clipboard, unreliable on mobile) to
 * "Save Image" on mobile, where the share sheet offers Save to Photos plus
 * direct sharing — and works inside in-app/wallet browsers.
 */
export function canShareNodeImage(): boolean {
  try {
    if (typeof navigator === 'undefined' || typeof window === 'undefined')
      return false;
    if (typeof navigator.share !== 'function') return false;
    if (typeof navigator.canShare !== 'function') return false;
    // Keep desktop on clipboard copy; only switch to the share sheet on
    // touch devices (where image clipboard writes are unreliable).
    const isTouch = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
    if (!isTouch) return false;
    const probe = new File([new Uint8Array()], 'probe.png', {
      type: 'image/png',
    });

    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/**
 * Opens the native share sheet with the rendered PNG. Throws `AbortError`
 * when the user dismisses the sheet (callers should treat that as a no-op).
 */
export async function shareNodeImage(
  node: HTMLElement,
  opts?: NodeToImageOptions & { fileName?: string; text?: string },
): Promise<void> {
  const { fileName, text, ...imageOpts } = opts ?? {};
  const file = await nodeToShareFile(node, fileName, imageOpts);
  const data: ShareData = { files: [file] };
  if (text) data.text = text;
  await navigator.share(data);
}
